import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";
import {
  faCircleCheck,
  faCircleXmark,
} from "@fortawesome/free-solid-svg-icons";

import Modal, { ModalBody, ModalFooter } from "../../components/Modal/Modal";
import { fetchEvent } from "../../services/events";
import {
  deactivateOrder,
  fetchOrderWithPatient,
  getPDF,
} from "../../services/orders";
import { AppDispatch, RootState } from "../../store";
import {
  addLocalOffsetToTimestamp,
  downloadFile,
  getEventFullAddress,
  getFormattedDate,
  getFormattedDateTime,
  getOrderPDFFileName,
  getPatientFullAddress,
  getPatientFullName,
} from "../../utils";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import ClosableBox from "../../components/Box/ClosableBox";
import OrderType from "../../ts/models/Order";
import classes from "./Order.module.css";
import Patient from "../../ts/models/Patient";
import Event from "../../ts/models/Event";
import Card from "../../components/Card/Card";
import { warningActions } from "../../store/slices/WarningSlice";
import { InsuranceStatus } from "../../ts/enums";
import Header from "../Header/Header";

const Order = () => {
  const { orderId } = useParams();
  const dispatch: AppDispatch = useDispatch();

  const { services, genders, relationships, ethnicGroups } = useSelector(
    (state: RootState) => state.masters
  );

  const [orderData, setOrderData] = useState<OrderType>();
  const [patientData, setPatientData] = useState<Patient>();
  const [eventData, setEventData] = useState<Event>();
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const getOrderData = async () => {
      try {
        const { status, data } = await fetchOrderWithPatient(orderId!);
        if (status === 200) {
          const patient = data.patient;
          const order = data.order;
          setPatientData(patient);
          setOrderData(order);

          const eventRes = await fetchEvent(order.eventId);
          if (eventRes.status === 200) {
            setEventData(eventRes.data);
          }
        }
      } catch (error) {
        console.log(error);
      }
    };

    if (orderId) getOrderData();
  }, [orderId]);

  const getServiceNames = () => {
    const selectedServiceIds = orderData?.services?.map(
      (service) => service.serviceId
    );

    const selectedServices = services.filter((service) =>
      selectedServiceIds?.includes(service.id)
    );

    const selectedServiceNames = selectedServices.map(
      (service) => service.name
    );

    return selectedServiceNames.join(", ");
  };

  const cancelOrderHandler = async () => {
    if (orderData?.id) {
      const { status, data } = await deactivateOrder({
        id: orderData?.id,
        isActive: false,
      });

      if (status === 200) {
        if (data.success === false && data.errorCode === "already_checked_in") {
          dispatch(
            warningActions.showWarning({
              message: "This order can not be cancelled.",
            })
          );
        } else {
          setOrderData(data);
        }
      }
    }
    setShowModal(false);
  };

  const downloadPDFHandler = () => {
    if (orderData?.id) {
      getPDF(orderData?.id)
        .then((response) => {
          const fileName = getOrderPDFFileName(orderData);
          downloadFile(response, fileName);
        })
        .catch((error) => {
          return error;
        });
    }
  };

  const gender =
    genders.find((gen) => gen.id === patientData?.gender)?.description || "";

  const ethnicity =
    ethnicGroups.find(
      (ethnicGroup) => ethnicGroup.id === patientData?.ethnicity
    )?.description || "";

  const relationship =
    relationships.find((rel) => rel.id === orderData?.relationshipToPatient)
      ?.description || "";

  return (
    <>
      {showModal && (
        <Modal title={<>Cancel Order ID: {`${orderData?.code}`}</>}>
          <>
            <ModalBody>
              <div className={classes.modalBody}>
                Are you sure you want to cancel this order? You can not revert
                this action.
              </div>
            </ModalBody>
            <ModalFooter>
              <div className={classes.actions}>
                <PrimaryButton
                  name='No'
                  onClick={() => setShowModal(false)}
                  className={classes.backBtn}
                />
                <PrimaryButton
                  name='Yes'
                  onClick={cancelOrderHandler}
                  className={classes.cancelBtn}
                />
              </div>
            </ModalFooter>
          </>
        </Modal>
      )}
      <Header />
      <div className={classes.main}>
        <div className={classes.orderCode}>
          {orderData && <div>Order {orderData?.code}</div>}
          {orderData?.code &&
            (orderData?.isActive ? (
              <div className={classes.confirmed}>
                <FontAwesomeIcon icon={faCircleCheck} /> Confirmed
              </div>
            ) : (
              <div className={classes.cancel}>
                <FontAwesomeIcon icon={faCircleXmark} />
                Canceled
              </div>
            ))}
        </div>
        <div className={classes.container}>
          <div className={classes.boxContainer}>
            <Card className={classes.informationCard}>
              <div className={classes.information}>
                <div className={classes.informationHeading}>
                  Patient's information
                </div>
                <div className={classes.informationContainer}>
                  <DataField
                    name='Full Name'
                    value={patientData ? getPatientFullName(patientData) : ""}
                  />
                  <DataField name='Gender' value={gender} />
                  <DataField name='Ethnic Group' value={ethnicity} />
                  <DataField
                    name='DOB'
                    value={
                      typeof patientData?.dob === "number"
                        ? getFormattedDate(
                            addLocalOffsetToTimestamp(patientData?.dob)
                          )
                        : ""
                    }
                  />
                  <DataField
                    name='Address'
                    value={
                      patientData ? getPatientFullAddress(patientData) : ""
                    }
                  />
                </div>
              </div>
              <ClosableBox name='Insurance Details' arrowIcon defaultState>
                <div className={classes.insuranceContainer}>
                  <DataField
                    name='Have insurance'
                    value={
                      patientData?.haveInsurance
                        ? InsuranceStatus.Yes
                        : InsuranceStatus.No
                    }
                  />
                  {patientData?.haveInsurance && (
                    <>
                      <DataField
                        name='Provider Name'
                        value={patientData?.insuranceDetails.providerName}
                      />
                      <DataField
                        name='Group Number'
                        value={patientData?.insuranceDetails.groupNumber}
                      />
                      <DataField
                        name='Policy Number'
                        value={patientData?.insuranceDetails.policyNumber}
                      />
                    </>
                  )}
                </div>
              </ClosableBox>
            </Card>
          </div>
          <div className={classes.orderContainer}>
            <Card>
              <div className={classes.orderItemHeading}>Ordered items</div>
              <div className={classes.itemsContainer}>
                <DataField name='Phone Number' value={orderData?.regPhoneNo} />
                <DataField name='Email' value={orderData?.regEmail} />
                <DataField
                  name='Relationship to patient'
                  value={relationship}
                />
                <DataField
                  name='Event Address'
                  value={eventData ? getEventFullAddress(eventData) : ""}
                />
                <DataField
                  name='Appointment Date'
                  value={
                    orderData?.appointmentTime
                      ? getFormattedDate(
                          addLocalOffsetToTimestamp(orderData.appointmentTime)
                        )
                      : ""
                  }
                />
                <DataField
                  name='Ordered On'
                  value={
                    orderData?.creationTime
                      ? getFormattedDateTime(orderData?.creationTime)
                      : ""
                  }
                />
                <DataField name='Services' value={getServiceNames()} />
              </div>
              {orderData?.isActive ? (
                <div className={classes.downloadPdfContainer}>
                  <PrimaryButton
                    name='Download PDF'
                    onClick={downloadPDFHandler}
                    className={classes.downloadPdfBtn}
                  />
                </div>
              ) : (
                <></>
              )}
            </Card>
          </div>
        </div>
        {orderData?.isActive ? (
          <div className={classes.cancelOrderContainer}>
            <Card>
              <div className={classes.cancelHeading}>Cancel your order</div>
              <div className={classes.cancelText}>
                Click the button below to cancel your order
              </div>
              <PrimaryButton
                name='Cancel order'
                onClick={() => {
                  setShowModal(true);
                }}
                className={classes.cancelBtn}
              />
            </Card>
          </div>
        ) : (
          <></>
        )}
      </div>
    </>
  );
};

export default Order;

const DataField = ({ name, value }: { name?: string; value?: string }) => {
  return (
    <>
      <div className={classes.name}>{name}</div>
      <div>:</div>
      <div className={classes.value}>{value}</div>
    </>
  );
};
